import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppShell } from './AppShell.js';
import {
  HomeRedirect,
  RequireAnyPermission,
  RequireAuth,
  RequirePasswordChange,
  RequirePermission,
} from './guards.js';
import { TabsProvider } from '../lib/tabs.js';
import { LoginPage } from '../modules/auth/LoginPage.js';

/**
 * Route-level code splitting from the first route (§5.3). Login is deliberately not lazy:
 * it is the one route an anonymous visitor always needs, and making them wait for a second
 * chunk to sign in is a poor trade.
 */
const DashboardPage = lazy(() =>
  import('../dashboard/DashboardPage.js').then((m) => ({ default: m.DashboardPage })),
);
const ManageDashboardsPage = lazy(() =>
  import('../dashboard/ManageDashboardsPage.js').then((m) => ({ default: m.ManageDashboardsPage })),
);
const MetricPage = lazy(() =>
  import('../dashboard/MetricPage.js').then((m) => ({ default: m.MetricPage })),
);
const DefinitionsPage = lazy(() =>
  import('../dashboard/DefinitionsPage.js').then((m) => ({ default: m.DefinitionsPage })),
);
const ResearchExportPage = lazy(() =>
  import('../modules/research/ResearchExportPage.js').then((m) => ({
    default: m.ResearchExportPage,
  })),
);
const SavedReportOpen = lazy(() =>
  import('../dashboard/SavedReportOpen.js').then((m) => ({ default: m.SavedReportOpen })),
);
const TimeStandardsAdminPage = lazy(() =>
  import('../modules/timeStandards/TimeStandardsAdminPage.js').then((m) => ({
    default: m.TimeStandardsAdminPage,
  })),
);
const TimeStandardsReportPage = lazy(() =>
  import('../modules/timeStandards/TimeStandardsReportPage.js').then((m) => ({
    default: m.TimeStandardsReportPage,
  })),
);
const FunderReportPage = lazy(() =>
  import('../modules/funder/FunderReportPage.js').then((m) => ({ default: m.FunderReportPage })),
);
const SavedReportsPage = lazy(() =>
  import('../dashboard/SavedReportsPage.js').then((m) => ({ default: m.SavedReportsPage })),
);
const DashboardReportPage = lazy(() =>
  import('../dashboard/ReportPage.js').then((m) => ({ default: m.ReportPage })),
);
const UsersPage = lazy(() =>
  import('../modules/users/UsersPage.js').then((m) => ({ default: m.UsersPage })),
);
const OutboxPage = lazy(() =>
  import('../modules/outbox/OutboxPage.js').then((m) => ({ default: m.OutboxPage })),
);
const AuditLogPage = lazy(() =>
  import('../modules/auditLog/AuditLogPage.js').then((m) => ({ default: m.AuditLogPage })),
);
const RolesPage = lazy(() =>
  import('../modules/roles/RolesPage.js').then((m) => ({ default: m.RolesPage })),
);
const CreateRolePage = lazy(() =>
  import('../modules/roles/CreateRolePage.js').then((m) => ({ default: m.CreateRolePage })),
);
const RoleDetailPage = lazy(() =>
  import('../modules/roles/RoleDetailPage.js').then((m) => ({ default: m.RoleDetailPage })),
);
const GridPage = lazy(() =>
  import('../modules/grid/GridPage.js').then((m) => ({ default: m.GridPage })),
);
const QueryWorkspacePage = lazy(() =>
  import('../modules/query/QueryWorkspacePage.js').then((m) => ({
    default: m.QueryWorkspacePage,
  })),
);
const ParticipantsPage = lazy(() =>
  import('../modules/participants/ParticipantsPage.js').then((m) => ({
    default: m.ParticipantsPage,
  })),
);
const CreateParticipantPage = lazy(() =>
  import('../modules/participants/CreateParticipantPage.js').then((m) => ({
    default: m.CreateParticipantPage,
  })),
);
const ParticipantDetailPage = lazy(() =>
  import('../modules/participants/ParticipantDetailPage.js').then((m) => ({
    default: m.ParticipantDetailPage,
  })),
);
const ReportsPage = lazy(() =>
  import('../modules/reports/ReportsPage.js').then((m) => ({ default: m.ReportsPage })),
);
const ReportDetailPage = lazy(() =>
  import('../modules/reports/ReportDetailPage.js').then((m) => ({
    default: m.ReportDetailPage,
  })),
);
const BatchesPage = lazy(() =>
  import('../modules/ingest/BatchesPage.js').then((m) => ({ default: m.BatchesPage })),
);
const StartImportPage = lazy(() =>
  import('../modules/ingest/StartImportPage.js').then((m) => ({ default: m.StartImportPage })),
);
const BatchDetailPage = lazy(() =>
  import('../modules/ingest/BatchDetailPage.js').then((m) => ({ default: m.BatchDetailPage })),
);
const GuidedImportPage = lazy(() =>
  import('../modules/ingest/guided/GuidedImportPage.js').then((m) => ({
    default: m.GuidedImportPage,
  })),
);
const DuplicatesPage = lazy(() =>
  import('../modules/duplicates/DuplicatesPage.js').then((m) => ({ default: m.DuplicatesPage })),
);
const UnverifiedPage = lazy(() =>
  import('../modules/duplicates/UnverifiedPage.js').then((m) => ({ default: m.UnverifiedPage })),
);
const CompareRecordsPage = lazy(() =>
  import('../modules/duplicates/CompareRecordsPage.js').then((m) => ({
    default: m.CompareRecordsPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import('../modules/admin/AdminUsersPage.js').then((m) => ({ default: m.AdminUsersPage })),
);
const CreateUserPage = lazy(() =>
  import('../modules/admin/CreateUserPage.js').then((m) => ({ default: m.CreateUserPage })),
);
const UserDetailPage = lazy(() =>
  import('../modules/admin/UserDetailPage.js').then((m) => ({ default: m.UserDetailPage })),
);
const EntryDefaultsPage = lazy(() =>
  import('../modules/entryDefaults/EntryDefaultsPage.js').then((m) => ({
    default: m.EntryDefaultsPage,
  })),
);
const RecordManagementPage = lazy(() =>
  import('../modules/records/RecordManagementPage.js').then((m) => ({
    default: m.RecordManagementPage,
  })),
);
const CategoryValuesPage = lazy(() =>
  import('../modules/records/CategoryValuesPage.js').then((m) => ({
    default: m.CategoryValuesPage,
  })),
);
const AdminSettingsPage = lazy(() =>
  import('../modules/admin/AdminSettingsPage.js').then((m) => ({ default: m.AdminSettingsPage })),
);
const ChangePasswordPage = lazy(() =>
  import('../modules/admin/ChangePasswordPage.js').then((m) => ({
    default: m.ChangePasswordPage,
  })),
);
const FacilitiesPage = lazy(() =>
  import('../modules/facilities/FacilitiesPage.js').then((m) => ({ default: m.FacilitiesPage })),
);
const CreateFacilityPage = lazy(() =>
  import('../modules/facilities/CreateFacilityPage.js').then((m) => ({
    default: m.CreateFacilityPage,
  })),
);
const FacilityDetailPage = lazy(() =>
  import('../modules/facilities/FacilityDetailPage.js').then((m) => ({
    default: m.FacilityDetailPage,
  })),
);
const ReviewQueuePage = lazy(() =>
  import('../modules/reviewQueue/ReviewQueuePage.js').then((m) => ({
    default: m.ReviewQueuePage,
  })),
);
const ReviewDetailPage = lazy(() =>
  import('../modules/reviewQueue/ReviewDetailPage.js').then((m) => ({
    default: m.ReviewDetailPage,
  })),
);
const MeasureEntriesPage = lazy(() =>
  import('../modules/measureEntries/MeasureEntriesPage.js').then((m) => ({
    default: m.MeasureEntriesPage,
  })),
);
const CreateMeasureEntryPage = lazy(() =>
  import('../modules/measureEntries/CreateMeasureEntryPage.js').then((m) => ({
    default: m.CreateMeasureEntryPage,
  })),
);
const MeasureEntryDetailPage = lazy(() =>
  import('../modules/measureEntries/MeasureEntryDetailPage.js').then((m) => ({
    default: m.MeasureEntryDetailPage,
  })),
);
const FamiliesPage = lazy(() =>
  import('../modules/families/FamiliesPage.js').then((m) => ({ default: m.FamiliesPage })),
);
const CreateFamilyPage = lazy(() =>
  import('../modules/families/CreateFamilyPage.js').then((m) => ({ default: m.CreateFamilyPage })),
);
const FamilyDetailPage = lazy(() =>
  import('../modules/families/FamilyDetailPage.js').then((m) => ({ default: m.FamilyDetailPage })),
);
const CasesPage = lazy(() =>
  import('../modules/cases/CasesPage.js').then((m) => ({ default: m.CasesPage })),
);
const CreateCasePage = lazy(() =>
  import('../modules/cases/CreateCasePage.js').then((m) => ({ default: m.CreateCasePage })),
);
const CaseDetailPage = lazy(() =>
  import('../modules/cases/CaseDetailPage.js').then((m) => ({ default: m.CaseDetailPage })),
);
const StatusPage = lazy(() =>
  import('../modules/status/StatusPage.js').then((m) => ({ default: m.StatusPage })),
);

function page(node: ReactNode) {
  return <Suspense fallback={<p className="text-muted">Loading…</p>}>{node}</Suspense>;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        {/*
          Inside the guard on purpose: RequireAuth swaps its children for a redirect the
          moment the session ends, so the provider unmounts and the open-record tabs cease
          to exist. Clearing them on logout is the component's lifetime, not cleanup code
          somebody has to remember to write.
        */}
        {/*
          Inside RequireAuth and outside everything else: a required password change blocks
          every route but its own, and a forced change that could be clicked past is not one.
        */}
        <RequirePasswordChange>
          <TabsProvider>
            <AppShell />
          </TabsProvider>
        </RequirePasswordChange>
      </RequireAuth>
    ),
    children: [
      // A Funder has no dashboard (slice 13): they land on their reports.
      { index: true, element: <HomeRedirect /> },
      {
        path: 'dashboard',
        // The guard is UX; /api/v1/* enforces the same permission independently.
        element: page(
          <RequirePermission permission="dashboard:read">
            <DashboardPage />
          </RequirePermission>,
        ),
      },
      /**
       * The report a dashboard segment opens (DASHBOARD-PLAN §7). Behind the dashboard's own guard; its
       * total and records need `measure:read`, which the API checks per request.
       */
      /** The dashboards other people see (DASHBOARD-PLAN D-7). The API refuses without the permission too. */
      {
        path: 'dashboard/manage',
        element: page(
          <RequirePermission permission="dashboard:manage">
            <ManageDashboardsPage />
          </RequirePermission>,
        ),
      },
      {
        // A provisional metric: its rule, its periods, its files (COUNTY-DEFINITIONS-NEEDED).
        path: 'dashboard/metrics/:key',
        element: page(
          <RequirePermission permission="dashboard:read">
            <MetricPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'funder-reports',
        element: page(
          <RequireAnyPermission permissions={['funder:read', 'dashboard:read']}>
            <FunderReportPage />
          </RequireAnyPermission>,
        ),
      },
      {
        path: 'research-export',
        element: page(
          <RequirePermission permission="participant:read">
            <RequirePermission permission="measure:read">
              <ResearchExportPage />
            </RequirePermission>
          </RequirePermission>,
        ),
      },
      {
        // Where a scheduled report's email links (slice 11).
        path: 'dashboard/saved-reports/:id',
        element: page(
          <RequirePermission permission="dashboard:read">
            <SavedReportOpen />
          </RequirePermission>,
        ),
      },
      {
        path: 'dashboard/saved-reports',
        element: page(
          <RequirePermission permission="dashboard:read">
            <SavedReportsPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'dashboard/report',
        element: page(
          <RequirePermission permission="dashboard:read">
            <DashboardReportPage />
          </RequirePermission>,
        ),
      },
      /**
       * The query workspace (QUERY-WORKSPACE-PLAN §4).
       *
       * No permission on the route, deliberately: the page offers only the panels the caller can read,
       * and every panel's request carries its own endpoint's permission. Gating the page on one
       * entity's permission would hide the whole workspace from somebody who can legitimately search
       * another — and gating it on "any of eight" is a rule that would drift from the eight.
       */
      { path: 'query', element: page(<QueryWorkspacePage />) },
      /**
       * Grid entry (GRID-ENTRY-PLAN.md).
       *
       * No permission on the route, for the same reason the query workspace has none: the page offers only
       * the kinds of record the caller may both read and write, and every row commits through the service
       * that carries that entity's own permission. Gating it on one entity's permission would hide the
       * whole page from somebody who can legitimately enter the other.
       */
      { path: 'grid', element: page(<GridPage />) },
      {
        path: 'participants',
        element: page(
          <RequirePermission permission="participant:read">
            <ParticipantsPage />
          </RequirePermission>,
        ),
      },
      {
        // Before :id, so 'new' is not read as an identifier.
        path: 'participants/new',
        element: page(
          <RequirePermission permission="participant:write">
            <CreateParticipantPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'participants/:id',
        element: page(
          <RequirePermission permission="participant:read">
            <ParticipantDetailPage />
          </RequirePermission>,
        ),
      },
      /**
       * Duplicate resolution. Both routes sit behind `participant:merge`, which is in no role
       * anybody is given — a merge cannot be undone, so an operator confers it deliberately.
       */
      {
        path: 'duplicates',
        element: page(
          <RequirePermission permission="participant:merge">
            <DuplicatesPage />
          </RequirePermission>,
        ),
      },
      /** The cleanup queue for auto-created people (PARTICIPANT-ID-PLAN D-8): the merge's other door. */
      {
        path: 'unverified',
        element: page(
          <RequirePermission permission="participant:merge">
            <UnverifiedPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'duplicates/:id',
        element: page(
          <RequirePermission permission="participant:merge">
            <CompareRecordsPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'reports',
        element: page(
          <RequirePermission permission="report:read">
            <ReportsPage />
          </RequirePermission>,
        ),
      },
      {
        // Linking and retracting need report:write; the API enforces that on the actions, so
        // a reader reaches the page and simply has no action bar.
        path: 'reports/:id',
        element: page(
          <RequirePermission permission="report:read">
            <ReportDetailPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'ingest',
        element: page(
          <RequirePermission permission="ingest:read">
            <BatchesPage />
          </RequirePermission>,
        ),
      },
      {
        // Before :id, so 'new' is not read as an identifier.
        path: 'ingest/new',
        element: page(
          <RequirePermission permission="ingest:write">
            <StartImportPage />
          </RequirePermission>,
        ),
      },
      {
        // Teaching the system to read an agency's file, by reading it. Needs ingest:write, because
        // it is how a template comes to exist.
        path: 'ingest/:id/guide',
        element: page(
          <RequirePermission permission="ingest:write">
            <GuidedImportPage />
          </RequirePermission>,
        ),
      },
      {
        // Mapping needs ingest:write and applying needs ingest:apply; both are enforced by the API
        // on the actions, so a reader reaches the page and sees the verification without controls.
        path: 'ingest/:id',
        element: page(
          <RequirePermission permission="ingest:read">
            <BatchDetailPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'facilities',
        element: page(
          <RequirePermission permission="facility:read">
            <FacilitiesPage />
          </RequirePermission>,
        ),
      },
      {
        // Before :id, so 'new' is not read as an identifier.
        path: 'facilities/new',
        element: page(
          <RequirePermission permission="facility:write">
            <CreateFacilityPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'facilities/:id',
        element: page(
          <RequirePermission permission="facility:read">
            <FacilityDetailPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'measures',
        element: page(
          <RequirePermission permission="measure:read">
            <MeasureEntriesPage />
          </RequirePermission>,
        ),
      },
      {
        // Before :id, so 'new' is not read as an identifier.
        path: 'measures/new',
        element: page(
          <RequirePermission permission="measure:write">
            <CreateMeasureEntryPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'measures/:id',
        element: page(
          <RequirePermission permission="measure:read">
            <MeasureEntryDetailPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'families',
        element: page(
          <RequirePermission permission="family:read">
            <FamiliesPage />
          </RequirePermission>,
        ),
      },
      {
        // Before :id, so 'new' is not read as an identifier.
        path: 'families/new',
        element: page(
          <RequirePermission permission="family:write">
            <CreateFamilyPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'families/:id',
        element: page(
          <RequirePermission permission="family:read">
            <FamilyDetailPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'cases',
        element: page(
          <RequirePermission permission="case:read">
            <CasesPage />
          </RequirePermission>,
        ),
      },
      {
        // Before :id, so 'new' is not read as an identifier.
        path: 'cases/new',
        element: page(
          <RequirePermission permission="case:write">
            <CreateCasePage />
          </RequirePermission>,
        ),
      },
      {
        path: 'cases/:id',
        element: page(
          <RequirePermission permission="case:read">
            <CaseDetailPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'review-queue',
        element: page(
          <RequirePermission permission="report:read">
            <ReviewQueuePage />
          </RequirePermission>,
        ),
      },
      {
        // Settling an item needs report:write; the API enforces that on the action, so a
        // reader reaches the page and sees it read-only rather than being bounced.
        path: 'review-queue/:id',
        element: page(
          <RequirePermission permission="report:read">
            <ReviewDetailPage />
          </RequirePermission>,
        ),
      },
      { path: 'change-password', element: page(<ChangePasswordPage />) },
      {
        // The whole admin section behind user:write — the permission only Admin holds among
        // the seeded roles, so Staff and ViewOnly neither see nor reach it.
        path: 'admin/users',
        element: page(
          <RequirePermission permission="user:write">
            <AdminUsersPage />
          </RequirePermission>,
        ),
      },
      {
        // Before :id, so 'new' is not read as an identifier.
        path: 'admin/users/new',
        element: page(
          <RequirePermission permission="user:write">
            <CreateUserPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'admin/users/:id',
        element: page(
          <RequirePermission permission="user:write">
            <UserDetailPage />
          </RequirePermission>,
        ),
      },
      /**
       * The choice lists, behind `lookup:write` rather than `user:write`.
       *
       * Every role holds `lookup:read` — a form cannot render a dropdown without it — so the read
       * permission says nothing about who may change one. `lookup:write` is in Admin and nowhere else
       * (RECORD-MANAGEMENT-PLAN D-4), and deliberately not dark: the point of GEN-02 is that the
       * customer does not need us for this.
       */
      {
        path: 'admin/entry-defaults',
        element: page(
          <RequirePermission permission="lookup:write">
            <EntryDefaultsPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'admin/records',
        element: page(
          <RequirePermission permission="lookup:write">
            <RecordManagementPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'admin/records/:category',
        element: page(
          <RequirePermission permission="lookup:write">
            <CategoryValuesPage />
          </RequirePermission>,
        ),
      },
      {
        // Every change and security event this org has recorded (OCC-33). Read-only.
        path: 'admin/change-log',
        element: page(
          <RequirePermission permission="audit:read">
            <AuditLogPage />
          </RequirePermission>,
        ),
      },
      {
        // Every email sent or only logged (slice 11).
        path: 'admin/outbox',
        element: page(
          <RequirePermission permission="audit:read">
            <OutboxPage />
          </RequirePermission>,
        ),
      },
      {
        /**
         * The provisional definitions every metric is computed from. Changing one changes published numbers,
         * so it is its own grant, `definition:manage`, held by Admin.
         */
        path: 'admin/definitions',
        element: page(
          <RequirePermission permission="definition:manage">
            <DefinitionsPage />
          </RequirePermission>,
        ),
      },
      {
        // The time standards' windows (slice 12): rules, like the definitions.
        path: 'admin/time-standards',
        element: page(
          <RequirePermission permission="definition:manage">
            <TimeStandardsAdminPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'time-standards',
        element: page(
          <RequirePermission permission="case:read">
            <TimeStandardsReportPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'admin/settings',
        element: page(
          <RequirePermission permission="user:write">
            <AdminSettingsPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'users',
        element: page(
          <RequirePermission permission="user:read">
            <UsersPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'roles',
        element: page(
          <RequirePermission permission="role:read">
            <RolesPage />
          </RequirePermission>,
        ),
      },
      {
        path: 'roles/new',
        element: page(
          <RequirePermission permission="role:write">
            <CreateRolePage />
          </RequirePermission>,
        ),
      },
      {
        // The dashboard's roles tile already linked here, to a page that did not exist yet.
        path: 'roles/:id',
        element: page(
          <RequirePermission permission="role:read">
            <RoleDetailPage />
          </RequirePermission>,
        ),
      },
      { path: 'status', element: page(<StatusPage />) },
      { path: '*', element: <p className="text-muted">No such page.</p> },
    ],
  },
]);
